import {
    applyMiddleware,
    compose,
    createStore,
    Store,
    Unsubscribe,
} from 'redux';
import { gameReducer} from './Game/GameReducer';
import Environment from '../Utility/Environment';
import thunk from 'redux-thunk';
import { IGameState } from './Game/IGameState';
import createSagaMiddleware from 'redux-saga';
import { rootSaga } from './rootSaga';
import { ActionsAwaiter } from './ActionsAwaiter';

// import { sentryActionLogger } from './SentryMiddleware';

export interface IStateListener<T> {
    selector(state: IGameState): T;
    callback(state: T): void;
}

export default class StateManager {
    private store: Store;
    private unsubscribe: Unsubscribe;

    private listeners: IStateListener<any>[];
    private previousState: IGameState;
    private readonly actionsAwaiter: ActionsAwaiter;
    public readonly waitFor: InstanceType<typeof ActionsAwaiter>['waitFor'];

    constructor() {
        this.listeners = [];

        let composeEnhancers: any;

        if (
            process &&
            process.env.NODE_ENV === Environment.DEVELOPMENT &&
            (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
        ) {
            composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__(
                {
                    maxAge: 200,
                    trace: true,
                    traceLimit: 30,
                }
            );
        } else {
            composeEnhancers = compose;
        }

        const sagaMiddleware = createSagaMiddleware();
        const enhancer = composeEnhancers(applyMiddleware(thunk, sagaMiddleware));

        this.store = createStore(gameReducer, enhancer);

        this.previousState = this.getState();

        this.actionsAwaiter = new ActionsAwaiter();
        this.waitFor = this.actionsAwaiter.waitFor.bind(this.actionsAwaiter);

        this.unsubscribe = this.store.subscribe(this.onStateChanged.bind(this));
        sagaMiddleware.run(rootSaga);
    }

    private onStateChanged(): void {
        this.listeners.forEach(listener => {
            listener.callback(listener.selector(this.getState()));
        });
    }

    public addListener<T>(listener: IStateListener<T>): void {
        if (this.listeners.indexOf(listener) !== -1) return;
        this.listeners.push(listener);
    }

    public removeListener(listener: IStateListener<any>): void {
        const index = this.listeners.indexOf(listener);
        if (index >= 0) this.listeners.splice(index, 1);
    }

    /**
     * Dispatches an action. This is the only way to trigger a state change.
     * @param action A plain object representing "what changed".
     * @returns For convenience, the same action object you dispatched.
     */
    public dispatch(action: any): any {
        this.actionsAwaiter.onAction(action);
        this.previousState = this.getState();
        return this.store.dispatch(action);
    }

    /**
     * Reads the state tree managed by the store.
     * @returns The current state tree of the game.
     */
    public getState(): IGameState {
        return this.store.getState();
    }

    /**
     * Get the state before the most most recent dispatch.
     * @deprecated Warning: Previous state can be unpredictable due to asynchronous dispatches/thunks etc.
     * If checking if a state value has changed consider using a 'local' copy of the value you are checking.
     */
    public getPreviousState(): IGameState {
        return this.previousState;
    }
}
